import React, { useContext, useState } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Button, Form, Card, Badge } from "react-bootstrap";
import { FloatCardComponent } from "../../components/cards";
import GastosTableComponent from "../../components/tables/GastosTableComponent";
import { ButtonComponent } from "../../components/elements";
import PageLayout from "../../layouts/PageLayout";
import products from "../../assets/data/gastos.json";

export default function GastosListPage() {

    const { t } = useContext(TranslatorContext)
    
    // Estado para el año seleccionado
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    
    // Datos de ejemplo por año (normalmente vendrían de la API)
    const [datosAnuales, setDatosAnuales] = useState({
        2024: {
            presupuestoTotal: 12000000,
            totalIngresos: 9600000,
            totalGastos: 5500000,
            fondosActivos: 4
        },
        2025: {
            presupuestoTotal: 15000000,
            totalIngresos: 11200000,
            totalGastos: 6800000,
            fondosActivos: 5
        },
        2023: {
            presupuestoTotal: 10000000,
            totalIngresos: 8500000,
            totalGastos: 4200000,
            fondosActivos: 3
        }
    });

    // Calcular datos dinámicos basados en el año seleccionado
    const datosYear = datosAnuales[selectedYear] || datosAnuales[2025];
    const saldoDisponible = datosYear.totalIngresos - datosYear.totalGastos;
    const porcentajeEjecucion = (datosYear.totalGastos / datosYear.presupuestoTotal) * 100;

    const floats = [
        { 
            "title": "presupuesto anual", 
            "digit": datosYear.presupuestoTotal, 
            "icon": "account_balance", 
            "variant": "lg blue" 
        }, 
        { 
            "title": "total ingresos", 
            "digit": datosYear.totalIngresos, 
            "icon": "trending_up", 
            "variant": "lg green" 
        }, 
        { 
            "title": "total gastos", 
            "digit": datosYear.totalGastos, 
            "icon": "trending_down", 
            "variant": "lg red" 
        },
        { 
            "title": "saldo disponible", 
            "digit": saldoDisponible, 
            "icon": "account_balance_wallet", 
            "variant": saldoDisponible > 0 ? "lg green" : "lg orange" 
        }
    ]

    // Generar años disponibles (últimos 5 años)
    const generarAniosDisponibles = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear; i >= currentYear - 4; i--) {
            years.push(i);
        }
        return years;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Gestión de Gastos</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">Administración</Link></li>
                                <li className="mc-breadcrumb-item">Gastos</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Selector de Año y Controles */}
                <Col xl={12}>
                    <Card className="mc-card mb-4">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Año de Gestión</Form.Label>
                                        <Form.Select 
                                            value={selectedYear} 
                                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                            className="form-select-lg"
                                        >
                                            {generarAniosDisponibles().map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <div className="text-center">
                                        <h6 className="text-muted mb-1">Presupuesto Anual</h6>
                                        <h4 className="text-primary mb-0">{formatCurrency(datosYear.presupuestoTotal)}</h4>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="text-center">
                                        <h6 className="text-muted mb-1">% Ejecución</h6>
                                        <h4 className={`mb-0 ${porcentajeEjecucion > 80 ? 'text-danger' : porcentajeEjecucion > 60 ? 'text-warning' : 'text-success'}`}>
                                            {porcentajeEjecucion.toFixed(1)}%
                                        </h4>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="text-center">
                                        <h6 className="text-muted mb-1">Fondos Activos</h6>
                                        <h4 className="text-info mb-0">{datosYear.fondosActivos}</h4>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
                
                {/* Cards de resumen */}
                {floats.map((float, index) => (
                    <Col key={ index } sm={6} lg={3}>
                        <FloatCardComponent 
                            variant={ float.variant }
                            digit={formatCurrency(float.digit)}
                            title={t(float.title)}
                            icon={ float.icon }
                        />
                    </Col>
                ))}

                {/* Botones de acciones principales */}
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-card-body">
                            <Row>
                                <Col md={2}>
                                    <Link to="/gastos/registro" className="text-decoration-none">
                                        <Button variant="primary" className="w-100 mb-2">
                                            <i className="material-icons me-2">add</i>
                                            Registrar Gasto
                                        </Button>
                                    </Link>
                                </Col>
                                <Col md={2}>
                                    <Link to="/gastos/fondos" className="text-decoration-none">
                                        <Button variant="info" className="w-100 mb-2">
                                            <i className="material-icons me-2">account_balance</i>
                                            Gestión Fondos
                                        </Button>
                                    </Link>
                                </Col>
                                <Col md={2}>
                                    <Link to="/gastos/reportes" className="text-decoration-none">
                                        <Button variant="success" className="w-100 mb-2">
                                            <i className="material-icons me-2">assessment</i>
                                            Reportes
                                        </Button>
                                    </Link>
                                </Col>
                                <Col md={2}>
                                    <Link to="/gastos/configuracion" className="text-decoration-none">
                                        <Button variant="secondary" className="w-100 mb-2">
                                            <i className="material-icons me-2">settings</i>
                                            Configuración
                                        </Button>
                                    </Link>
                                </Col>
                                <Col md={2}>
                                    <Button variant="warning" className="w-100 mb-2">
                                        <i className="material-icons me-2">backup</i>
                                        Exportar {selectedYear}
                                    </Button>
                                </Col>
                                <Col md={2}>
                                    <Button variant="outline-primary" className="w-100 mb-2">
                                        <i className="material-icons me-2">upload_file</i>
                                        Importar Datos
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>

                {/* Estado de Fondos Rápido */}
                <Col xl={12}>
                    <Card className="mc-card mb-4">
                        <Card.Header>
                            <h5>Estado de Fondos - {selectedYear}</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <div className="d-flex align-items-center p-3 border rounded">
                                        <div className="me-3">
                                            <div className="bg-success rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                                                <i className="material-icons text-white">savings</i>
                                            </div>
                                        </div>
                                        <div>
                                            <h6 className="mb-1">Fondo Emergencia</h6>
                                            <small className="text-muted">85% disponible</small>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center p-3 border rounded">
                                        <div className="me-3">
                                            <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                                                <i className="material-icons text-white">business</i>
                                            </div>
                                        </div>
                                        <div>
                                            <h6 className="mb-1">Fondo Operativo</h6>
                                            <small className="text-muted">62% disponible</small>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center p-3 border rounded">
                                        <div className="me-3">
                                            <div className="bg-info rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                                                <i className="material-icons text-white">trending_up</i>
                                            </div>
                                        </div>
                                        <div>
                                            <h6 className="mb-1">Fondo Inversión</h6>
                                            <small className="text-muted">45% disponible</small>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center p-3 border rounded">
                                        <div className="me-3">
                                            <div className="bg-danger rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                                                <i className="material-icons text-white">group</i>
                                            </div>
                                        </div>
                                        <div>
                                            <h6 className="mb-1">Fondo Capacidad</h6>
                                            <small className="text-danger">27% disponible</small>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Tabla de gastos recientes */}
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header">
                            <h4 className="mc-card-title">Gastos Recientes - {selectedYear}</h4>
                            <div className="mc-card-header-option">
                                <Badge bg="primary" className="me-2">
                                    {products.tbody.length} registros
                                </Badge>
                                <Link to="/gastos/listado-completo">
                                    <ButtonComponent className="material-icons" type="button" variant="primary">
                                        visibility
                                    </ButtonComponent>
                                </Link>
                            </div>
                        </div>
                        <Row>
                            <Col xl={12}>
                                <GastosTableComponent 
                                    thead={ products.thead } 
                                    tbody={ products.tbody } 
                                />
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}
